import type { Context } from "react";
import { createContext, useContext } from "react";

type LocalContext<T, U extends string> = {
  [K in U as `${Capitalize<string & K>}Context`]: Context<T | null>;
} & { [K in U as `use${Capitalize<string & K>}Context`]: () => T };

/**
 * Helper function to scaffold local React context.
 *
 * ```ts
 * interface MenuContext {
 *   isOpen: boolean
 * }
 *
 * const { MenuContext, useMenuContext } = createLocalContext<MenuContext>()('Menu')
 * ```
 */
export function createLocalContext<T>() {
  return <U extends string>(name: U) => {
    const LocalContext = createContext<T | null>(null);

    function useLocalContext(): T {
      const context = useContext(LocalContext);

      if (context === null) {
        throw new Error(
          `Only use use${name}Context hook within a ${name}Context provider`
        );
      }

      return context;
    }

    return {
      [`${name}Context`]: LocalContext,
      [`use${name}Context`]: useLocalContext,
    } as LocalContext<T, U>;
  };
}
